// Client-side sales permission mirror (Module 09 §7, slice P3-A part 2).
//
// The server is the ONLY authorisation boundary: every §5 route carries one
// code and every cell narrowing (sales → assigned customers, finance → credit
// fields only, ga-only activation/guard/class edits) is re-checked in the
// service (D-040 two-layer). This hook exists so the screens do not OVER-SHOW
// controls a role cannot use — a planner opening the customer list should see
// the book, not a "New customer" button that 403s on submit.
//
// `has()` reads the caller's effective `sales.*` codes off the permission
// list `GET /v1/auth/tenants` carries (D-586). The screen AUDIENCES below are
// role sets because §4 writes them as role sets and the service narrows on
// them; they read the role's clone LINEAGE, as the service does (D-224), so a
// tenant's renamed clone of `sales` opens what `sales` opens, and each also
// requires the code its route checks, so a clone the tenant trimmed opens
// less. The few checks the service makes on the literal role (naming an
// owner) read the literal codes and say so. `SALES_ROLE_GRANTS` mirrors
// `systemRoleGrants` in packages/contracts and answers only for a feed row
// without the permission list. `has()` is FAIL-CLOSED — false until the feed
// settles, false if it fails.
//
// Registered in tools/ci/web-permission-mirror.ts (namespace `sales.`), so a
// cell here that drifts from the contract table fails the build.

import {
  derivePermissions,
  hasLineage,
  hasLiteralRole,
  roleSignalOf,
  useRoleCodesFeed,
  type AreaPermissions,
  type RoleInput,
  type RoleSignal,
} from "../../../../lib/role-codes";

/** The `sales.*` codes this area mirrors. Seven landed with P3-A; THREE more
 * with P3-C-1 — the QUOTE pair (`sales.quote.write`, `sales.quote.send`) and
 * `sales.order.cancel` — the slice mounting the §5 quote routes AND
 * `POST /v1/orders/{id}/cancel`; and ONE with P3-C-2 (D-263, 2026-09-08) —
 * `sales.override.write`, the §2.9 override surface's single write code
 * (create + revoke), mirrored in the change that mounts
 * `POST /v1/customer-price-overrides` and `POST …/{id}/revoke`. The pairing
 * rule: a code is mirrored in the same change that ships the routes it gates,
 * never before. And ONE with P3-D PR-D1 (D-283, 2026-09-15) —
 * `sales.order.write`, the direct order's own code, registered WITH
 * `POST /v1/orders` (spec 09 §7): held by ga, fa and sales (assigned
 * customers, service-narrowed), and the write gate of the §4.4 composer's
 * order mode. It is distinct from `sales.quote.send` because the two are
 * different policy surfaces — accept-manual commits the tenant to an offer a
 * customer was SENT and said yes to; a direct order commits it to a document
 * no customer ever saw.
 *
 * `sales.override.write` IS HELD BY sales AND gates a verb sales may not use:
 * revoke is narrowed service-side to facility_admin / group_admin (D-040 two
 * layers), so a screen that offers Revoke on `has("sales.override.write")`
 * alone offers a 403 to every sales user. The Pricing tab gates Revoke on the
 * ROLE (`isAdmin`) and Propose on the code — the same split the credit cell
 * already makes on `manage_customers`.
 *
 * `sales.order.confirm` STAYS ABSENT, and not merely because its slice has not
 * come: migration 0055 deliberately does not seed it at all. Manual acceptance
 * mounts under `sales.quote.send` (it is the dispatch half of the quote
 * lifecycle), so no route reads `order.confirm` and no role holds it. Mirroring
 * it would offer the UI a grant the server has never heard of.
 *
 * AND TWO with P3-D PR-D3 (D-286, 2026-09-17), registered WITH the shipment
 * routes migration 0063 seeds them for — two codes by D-074's distinct-role-set
 * test, never one:
 *   * `sales.shipment.post` — open / pick / undo / pack / ship / cancel a
 *     shipment (`POST /v1/orders/{id}/shipments` + the five `/v1/shipments/
 *     {id}/*` mutations). Held by ga, fa and storekeeper (own facility,
 *     service-narrowed); cancel is narrowed to fa/ga SERVICE-side, the
 *     `sales.override.write` revoke shape. The floor's ship tile derives its
 *     grant through this mirror; the office offers only a LINK into the ship
 *     queue on it (one pick UI, on the floor — §4.8).
 *   * `sales.order.deliver` — `POST /v1/orders/{id}/mark-delivered`, the
 *     office's door on the carrier's word (§3.6 `shipped → delivered`). Held
 *     by ga, fa, sales (assigned customers) and storekeeper (own facility) —
 *     a WIDER set than `shipment.post` because sales may confirm a delivery
 *     but may not pick one, which is exactly why it is a second code.
 *
 * AND ONE with P3-D PR-D4 (D-288, 2026-09-17), registered WITH `POST
 * /v1/orders/{id}/override-status`, which migration 0064 seeds it for:
 * `sales.order.override_status`, §3.6's audited manual transition. §5's cell
 * for the route is facility_admin, plus group_admin by the platform invariant.
 * It is a new code because no existing sales code carries exactly {ga, fa}:
 * riding `sales.order.cancel` would hand every sales manager a forward status
 * override that §3.6 and §7 withhold from them (D-074's distinct-role-set
 * test). The facility fence is the service's (D-286 ruling 25), never a
 * missing grant here.
 *
 * The THREE PDF doors that land with it mint no code. They are reads under
 * `sales.read`, and the quote PDF and the proforma also need
 * `sales.read_value` in the service (D-288 ruling 2): a JSON read can redact a
 * money field and a PDF cannot. The screens gate those two links on
 * `sales.read_value` through this mirror, so a planner or a storekeeper is
 * never offered a document the server will refuse them.
 *
 * AND ONE with P3-E PR-E2 (D-292 ruling 2, 2026-09-18), registered WITH the
 * two task writes migration 0067 seeds it for: `sales.activity.write` —
 * `POST /v1/sales-activities` (a manual task) and `POST /v1/sales-activities/
 * {id}/complete`. Held by ga, fa and sales. Its role set equals the quote
 * pair's, and it is still its own code because it is a different policy
 * surface (D-283 ruling 1's test): a tenant may want juniors quoting without
 * owning follow-up work, or the reverse. The list itself reads under
 * `sales.read`, narrowed service-side; the complete route admits the
 * assignee or an admin, which is a SERVICE narrowing on this code (the task
 * screens offer Complete only on rows the server returned as the reader's
 * own, or to an admin — hooks/activity-queries.ts `canCompleteTask`).
 *
 * AND ONE with P3-E2 PR E2-1 (D-312 ruling 15, 2026-09-27), registered WITH the
 * deal writes migration 0073 seeds it for: `sales.opportunity.write` —
 * `POST /v1/opportunities`, `PATCH /v1/opportunities/{id}`, `POST …/{id}/move`,
 * `…/win`, `…/lose`, `…/reopen` and `POST /v1/quotes/{id}/opportunity`. Held by
 * ga, fa and sales: the task code's role set, and its own code all the same
 * because moving a deal is a different policy surface (D-283 ruling 1's test).
 * The deal READS ride `sales.read` under narrowing 15, whose audience is a ROLE
 * set (`PIPELINE_SCREEN_ROLES` below), and stage tuning rides
 * `sales.tier.manage`. Narrowing 16 (a sales principal writes only on an
 * assigned customer and a deal it reads; only an admin names another owner) is
 * the SERVICE's, so the screens offer the verbs on this code and the owner
 * control on `isAdmin`. */
export const SALES_PERMISSIONS = [
  "sales.read",
  "sales.manage_customers",
  "sales.tier.manage",
  "sales.margin.read",
  "sales.read_value",
  "sales.margin.approve",
  "sales.credit.approve",
  "sales.quote.write",
  "sales.quote.send",
  "sales.order.cancel",
  "sales.override.write",
  // P3-D PR-D1 (D-283) — eleven → twelve, one name: `sales.order.write`,
  // `POST /v1/orders`. Mirrored in the change that mounts the route and the
  // §4.4 composer's order mode (the pairing rule above).
  "sales.order.write",
  // P3-D PR-D3 (D-286) — twelve → FOURTEEN, two names, mirrored in the change
  // that mounts the shipment routes and `mark-delivered` (the pairing rule):
  //   * `sales.shipment.post` — the §3.7 floor path's six mutations.
  //   * `sales.order.deliver` — `POST /v1/orders/{id}/mark-delivered`.
  "sales.shipment.post",
  "sales.order.deliver",
  // P3-D PR-D4 (D-288) — FOURTEEN → FIFTEEN, one name, mirrored in the change
  // that mounts its route (the pairing rule):
  //   * `sales.order.override_status` — `POST /v1/orders/{id}/override-status`,
  //     §5's cell facility_admin (+ group_admin). Held by ga and fa ONLY.
  "sales.order.override_status",
  // P3-E PR-E2 (D-292) — FIFTEEN → SIXTEEN, one name, mirrored in the change
  // that mounts its routes (the pairing rule):
  //   * `sales.activity.write` — `POST /v1/sales-activities` and
  //     `POST /v1/sales-activities/{id}/complete`. Held by ga, fa and sales.
  "sales.activity.write",
  // P3-E2 PR E2-1 (D-312 ruling 15) — SIXTEEN → SEVENTEEN, one name, mirrored
  // in the change that mounts its routes (the pairing rule):
  //   * `sales.opportunity.write` — the seven deal writes (create, edit, move,
  //     win, lose, reopen, and the quote link). Held by ga, fa and sales.
  "sales.opportunity.write",
  // P3-J J-3 (D-464 ruling 2, D-469) — SEVENTEEN → EIGHTEEN, one name,
  // mirrored in the change that draws Re-promise (the pairing rule):
  //   * `sales.order.re_promise` — `POST /v1/orders/{id}/re-promise` and
  //     module 03's withdraw of a committed or at-risk promise. Held by ga,
  //     fa and planner; sales quotes and commits but never changes a date the
  //     customer was told.
  "sales.order.re_promise",
] as const;

/** The QUOTE pair, named once so the grant rows below read as §7's own table
 * row ("Quote create/edit/send — ga ✓, fa ✓, sales ✓, everyone else —"). */
const QUOTE_CODES = ["sales.quote.write", "sales.quote.send"] as const;

export type SalesPermission = (typeof SALES_PERMISSIONS)[number];

/**
 * Role code → `sales.*` codes: the sales slice of the contracts
 * `systemRoleGrants` table (spec 09 §7), transcribed cell by cell.
 *
 * The rows that are easy to get wrong:
 * - facility_admin holds ALL EIGHTEEN — the ga-only cells (price-list
 *   activation, margin-guard/off-cut-class edits), the §3.4 approve threshold,
 *   the override-revoke narrowing and the shipment-cancel narrowing are
 *   SERVICE-side narrowings on these codes, never missing grants. (The
 *   catalog spells fa's sales block out as an explicit list rather than
 *   deriving it — the mirror transcribes the list.)
 * - sales holds read + manage_customers + read_value + the quote pair +
 *   order.cancel + override.write + order.write + order.deliver, all narrowed
 *   to ASSIGNED customers service-side (and, for cancel, to their own
 *   confirmed orders; for override.write, to CREATE — revoke is fa/ga); NO
 *   margin.read (tenant-grantable), NO tier.manage, NO approve codes, and NO
 *   shipment.post — a manager confirms a delivery, a storekeeper picks it —
 *   and NO order.override_status (P3-D PR-D4, D-288): §5's override cell is
 *   facility_admin, so ga and fa are the only rows that carry it.
 * - finance edits CREDIT fields through the same `manage_customers` code the
 *   profile editors use (field-group narrowing service-side) and holds both
 *   field grants plus credit.approve.
 * - qc holds `sales.read` for OTHER surfaces (§7's read-only orders row).
 *   The §4.1/§4.3 screen audiences are NARROWER role sets, mirrored
 *   separately below. Since D-337 the quote reads refuse qc and the
 *   storekeeper outright (§7 narrowing 23), so `sales.read` opens orders for
 *   them and no quote.
 * - storekeeper holds `sales.read` (ship-relevant reads: the ship queue and
 *   the order they are picking — §7 :976), `sales.shipment.post` and
 *   `sales.order.deliver` (P3-D PR-D3, D-286), each narrowed to their OWN
 *   facility service-side — never a missing grant here. The order BOOK is
 *   still not theirs (`ORDER_SCREEN_ROLES` below is unchanged).
 * - procurement, maintenance and floor_worker hold NOTHING here.
 */
export const SALES_ROLE_GRANTS: Readonly<Record<string, readonly string[]>> = {
  // ga and fa carry every mirrored code, which since P3-D PR-D4 includes
  // `sales.order.override_status`: §7's cell for the code and §5's
  // override-status row name facility_admin (+ group_admin by the platform
  // invariant), and §3.6 names facility_admin for both manual transitions
  // (D-288). They are the ONLY two rows that carry it; every row below leaves
  // it out.
  group_admin: [...SALES_PERMISSIONS],
  facility_admin: [...SALES_PERMISSIONS],
  // §7's Re-promise row is ✓ planner (P3-J J-3, D-464 ruling 2): the planner
  // re-promises a line and withdraws a committed promise.
  planner: ["sales.read", "sales.order.re_promise"],
  // §7's "Quote create/edit/send" row is ✓ for sales without qualification —
  // the narrowing is to ASSIGNED customers (and, per §3.4's send row, to the
  // quote's owner or a collaborator), which is a service-side row filter on
  // the same codes, never a missing grant.
  // §7's "Order cancel" cell is ✓ sales (confirmed only, own) — both
  // qualifiers are service-side narrowings on this code, never a missing grant.
  // §7's "Customer price override create" cell is ✓ sales (auto-routes to
  // approval on breach) — P3-C-2, D-263; the revoke narrowing is the
  // service's, see the header.
  // §7's "Direct order create" row is ✓ sales (assigned customers) — P3-D
  // PR-D1, D-283; the assignment narrowing is the service's D-224 lineage
  // 404, never a missing grant here.
  // §7's "Mark delivered" row is ✓ sales (assigned) — P3-D PR-D3, D-286; the
  // same D-224 404 narrows it. NOT `sales.shipment.post`: §7's pick/pack/ship
  // cell is storekeeper/fa/ga, and a manager has no bench to pick from.
  // §7's "Task create/complete" row is ✓ sales — P3-E PR-E2, D-292 ruling 2:
  // create for themselves only, complete their own; both narrowings are the
  // service's, never a missing grant here.
  sales: [
    "sales.read",
    "sales.manage_customers",
    "sales.read_value",
    ...QUOTE_CODES,
    "sales.order.cancel",
    "sales.override.write",
    "sales.order.write",
    "sales.order.deliver",
    "sales.activity.write",
    // §7's deal row is ✓ sales (assigned customers; deals it reads) — P3-E2 PR
    // E2-1, D-312 ruling 15. Narrowing 16 is the service's, never a missing
    // grant here; finance and planner READ every deal and write none.
    "sales.opportunity.write",
  ],
  procurement: [],
  finance: [
    "sales.read",
    "sales.manage_customers",
    "sales.margin.read",
    "sales.read_value",
    "sales.credit.approve",
  ],
  qc: ["sales.read"],
  maintenance: [],
  // §7's "Shipment pick/pack/ship" cell (storekeeper ✓ own facility) and the
  // "Mark delivered" row (storekeeper ✓ own facility) — P3-D PR-D3, D-286.
  // Both facility narrowings are the service's (`readArmsOf`'s facility arm,
  // the RESTRICTIVE fence on the shipment tables), never a missing grant.
  storekeeper: ["sales.read", "sales.shipment.post", "sales.order.deliver"],
  floor_worker: [],
};

/** The effective `sales.*` set (pure, unit-tested): the feed's permission
 * list cut to this slice, or the mirror without one (D-586). */
export function deriveSalesPermissions(roles: RoleInput): Set<string> {
  return derivePermissions(roles, SALES_ROLE_GRANTS);
}

/**
 * The §4.1 customer-screen AUDIENCE, mirrored as a ROLE SET (the waste
 * SETTINGS_READ_ROLES pattern) because it is deliberately NARROWER than the
 * `sales.read` holder set: qc and storekeeper hold the code for the order
 * read-only and ship-queue surfaces, not for the customer book (nor, since
 * D-337, for any quote read). The
 * service enforces exactly this; the mirror only decides what is offered.
 */
export const CUSTOMER_SCREEN_ROLES: ReadonlySet<string> = new Set([
  "group_admin",
  "facility_admin",
  "sales",
  "finance",
  "planner",
]);

/** The §4.3 pricing-screens audience (price lists / off-cut classes). */
export const PRICING_SCREEN_ROLES: ReadonlySet<string> = new Set([
  "group_admin",
  "facility_admin",
  "sales",
]);

/**
 * The §4.5 QUOTE-LIST and §4.4 composer audience — §4.5's own role line, which
 * is deliberately NARROWER than the `sales.read` holder set: "sales (own +
 * collaborator), facility_admin, group_admin".
 *
 * §7's "View quotes read-only" row admits planner and finance, and they are
 * not being hidden from quotes: they read them where §4.2 puts them, in the
 * customer 360's quote panel, whose audience is §4.1's (which includes finance
 * and planner). qc and the storekeeper are refused the quote reads by the
 * service since D-337 (§7 narrowing 23), so leaving them out here is the
 * server's own answer. The quote BOOK is the working queue of the
 * people who write quotes; a finance user opening it would get a list of
 * documents with no verb on any of them. The service enforces the same shape
 * either way (D-040 two layers), so this decides only what is offered.
 */
export const QUOTE_SCREEN_ROLES: ReadonlySet<string> = new Set([
  "group_admin",
  "facility_admin",
  "sales",
]);

/**
 * The PIPELINE audience (spec 09 §4.14, P3-E2 PR E2-1): the board, the deal
 * list, the deal page and the forecast. It is §7 narrowing 15 transcribed as a
 * ROLE set, because the service keys it on the authorising LINEAGE and
 * refuses every other lineage 403 even though qc and storekeeper hold
 * `sales.read` (D-312 ruling 11): ga and fa read every deal, sales reads its
 * own and its assigned customers', finance and planner read every deal. It is
 * the customer book's set (§4.1) by construction — a deal hangs off a
 * customer — and it is restated rather than aliased so a later change to
 * either audience cannot silently move the other.
 */
export const PIPELINE_SCREEN_ROLES: ReadonlySet<string> = new Set([
  "group_admin",
  "facility_admin",
  "sales",
  "finance",
  "planner",
]);

/**
 * The §4.6 REVIEW-INBOX audience — the only screen in the area whose audience
 * is a CODE set rather than a role set, because §4.6's own sentence names
 * codes: the decide actions are "permission-gated (`sales.margin.approve` /
 * `sales.credit.approve`)". A principal holding either code has cards to work;
 * a principal holding neither has an inbox that can only ever be empty, and an
 * always-empty inbox in the nav is the disabled shell D-088 forbids.
 *
 * finance therefore reaches it (credit releases are theirs, §7) while sales
 * does not — the exact inverse of the quote book, and both are right.
 */
export function canOpenReviews(has: (code: string) => boolean): boolean {
  // The inbox's own read rides `sales.read` (`reviews.controller.ts`), so a
  // role holding a decide code without the read is offered nothing (D-586).
  return has("sales.read") && (has("sales.margin.approve") || has("sales.credit.approve"));
}

/**
 * The §4.7 ORDER audience — the WIDEST screen set in the area, and the one
 * place §7's "View orders read-only" row is honoured in full: planner
 * (what must be made), finance (what is owed), qc (what shipped) all read
 * orders, and §4.7's own role line names all three.
 *
 * storekeeper is the one `sales.read` holder left out: §7 qualifies their row
 * as "ship-relevant", which is the ship queue and the pick screen on the
 * floor (`/floor/ship`, P3-D PR-D3 — gated there on `sales.shipment.post`
 * through this same mirror), not the order book. They get no link rather
 * than a book of documents whose every verb is somebody else's. UNCHANGED by
 * PR-D3 on purpose: the storekeeper's glass is the floor (§4.8).
 */
export const ORDER_SCREEN_ROLES: ReadonlySet<string> = new Set([
  "group_admin",
  "facility_admin",
  "sales",
  "planner",
  "finance",
  "qc",
]);

/** The §7 credit cell — who may flip the credit-hold toggle / edit limits.
 * Enforced service-side as a field-group narrowing on `manage_customers`. */
const CREDIT_EDIT_ROLES: ReadonlySet<string> = new Set([
  "group_admin",
  "facility_admin",
  "finance",
]);

/** Roles holding `identity.view_members` (the member directory read the owner
 * column resolves names through) AND the §5 admin cells (channel removal,
 * customer archive). NOT a `sales.*` code, so it stays out of
 * SALES_ROLE_GRANTS, whose CI mirror is against the sales slice — the same
 * shape as production's MEMBER_DIRECTORY_ROLES, for the same reason. */
const ADMIN_ROLES: ReadonlySet<string> = new Set(["group_admin", "facility_admin"]);

export interface SalesPermissions extends AreaPermissions, RoleSignal {
  readonly roleCodes: ReadonlySet<string>;
  /** True when the role feed itself failed — the screens say so and let the
   * request go, rendering the server's own refusal rather than a blank page. */
  readonly isFeedError: boolean;
  /** §4.1 audience: may open the customer list / 360. */
  readonly canOpenCustomers: boolean;
  /** §4.14 audience (P3-E2 PR E2-1): may open the board, the deal list and a
   * deal (`canOpenPipeline`, §7 narrowing 15's five lineages). */
  readonly canOpenPipeline: boolean;
  /** §4.3 audience: may open the price-list / off-cut screens. */
  readonly canOpenPricing: boolean;
  /** §4.5/§4.4 audience: may open the quote book and the composer. */
  readonly canOpenQuotes: boolean;
  /** §4.6 audience: holds either decide code, so has cards to work. */
  readonly canOpenReviews: boolean;
  /** §4.7 audience: may open the order book and an order. */
  readonly canOpenOrders: boolean;
  /** §7 credit cell: may flip credit hold / edit limits (ga/fa/finance). */
  readonly canEditCredit: boolean;
  /** Holds group_admin or facility_admin by its LITERAL code: the owner,
   * task-scope and analytics exemptions the service grants the literal admin
   * alone (D-224 ruling 7), and the member directory those screens read
   * names through. The admin verbs the service keys on the admin LINEAGE
   * (archive, merge, restore, redact, override revoke, channel removal) read
   * `isAdminLineage` instead (D-586). */
  readonly isAdmin: boolean;
  /** Stands on the group_admin or facility_admin lineage (D-224, D-586). */
  readonly isAdminLineage: boolean;
}

/**
 * The sales screen audiences and admin facts for a role signal (pure,
 * unit-tested). Each audience is the role set §4 names, read on the LINEAGE
 * the service's door reads (`customer-door.ts`, `quote-door.ts`,
 * `orders.service.ts`, `deal-door.ts`), together with `sales.read`, the code
 * every one of those routes carries, so a tenant's clone opens what its
 * source role opens and a clone without the read opens nothing (D-586).
 */
export function deriveSalesAudiences(
  roles: RoleInput,
  codes: ReadonlySet<string>,
): Omit<SalesEntryPermissions, "has"> & {
  readonly canEditCredit: boolean;
  readonly isAdmin: boolean;
  readonly isAdminLineage: boolean;
} {
  const reads = codes.has("sales.read");
  return {
    canOpenCustomers: reads && hasLineage(roles, CUSTOMER_SCREEN_ROLES),
    canOpenPipeline: reads && canOpenPipeline(roles),
    canOpenPricing: reads && hasLineage(roles, PRICING_SCREEN_ROLES),
    canOpenQuotes: reads && hasLineage(roles, QUOTE_SCREEN_ROLES),
    canOpenReviews: canOpenReviews((code: string) => codes.has(code)),
    canOpenOrders: reads && hasLineage(roles, ORDER_SCREEN_ROLES),
    // `customers.service.ts` checks credit fields on CREDIT_LINEAGES, under
    // the route's `sales.manage_customers`.
    canEditCredit: codes.has("sales.manage_customers") && hasLineage(roles, CREDIT_EDIT_ROLES),
    // LITERAL-ROLE-CODES: the owner, task-scope and analytics exemptions are literal server-side (`deal-door.ts`, `activities.service.ts`, `sales-stats.service.ts`, D-224 ruling 7).
    isAdmin: hasLiteralRole(roles, ADMIN_ROLES),
    isAdminLineage: hasLineage(roles, ADMIN_ROLES),
  };
}

export function useSalesPermissions(): SalesPermissions {
  const feed = useRoleCodesFeed();
  const codes = deriveSalesPermissions(feed);
  return {
    isResolved: feed.isResolved,
    has: (code: string) => codes.has(code),
    ...roleSignalOf(feed),
    isFeedError: feed.isFeedError,
    ...deriveSalesAudiences(feed, codes),
  };
}

// ---------------------------------------------------------------------------
// THE AREA'S ENTRY TABLE — one rule, read by the app nav AND the sub-nav
// (the waste-layout doctrine: two gating rules for one area is the defect).
// ---------------------------------------------------------------------------

export interface SalesAreaEntry {
  /** The route path. */
  readonly to: string;
  /** The dictionary key for its label (`sales.nav.*`). */
  readonly labelKey: string;
  /** Sibling addresses that also light this entry's tab, each with its
   * descendants (`RouterTabDef.activePaths`). */
  readonly activePaths?: readonly string[];
}

/** The permission facts the entry table reads — the shape both callers hold. */
export interface SalesEntryPermissions {
  readonly has: (code: string) => boolean;
  readonly canOpenCustomers: boolean;
  readonly canOpenPipeline: boolean;
  readonly canOpenPricing: boolean;
  readonly canOpenQuotes: boolean;
  readonly canOpenReviews: boolean;
  readonly canOpenOrders: boolean;
}

/**
 * The sales screens this principal may open, in landing order (pure).
 *
 * ORDER IS THE LANDING ORDER, and it is deliberate: the customer book first
 * (the screen that answers a question without an id in hand), then the pricing
 * configuration in governance order — price lists, off-cut discounts — and the
 * margin-guard editor last, offered only to `sales.tier.manage` holders
 * because its GET rides that code (a link into a dead 403 call is the D-088
 * defect this table exists to prevent).
 */
export function salesAreaEntries(perms: SalesEntryPermissions): SalesAreaEntry[] {
  const entries: SalesAreaEntry[] = [];
  if (perms.canOpenCustomers) {
    entries.push({ to: "/sales/customers", labelKey: "sales.nav.customers" });
  }
  // THE PIPELINE (P3-E2 PR E2-1, spec 09 §4.14) straight after Customers: a
  // deal hangs off a customer, and the board, the deal list and the forecast
  // share this one entry. It reads its own gate, §7 narrowing 15's five
  // lineages, which equals the customer book's set today and is restated
  // rather than aliased, so a later change to either cannot move the other.
  // It can never be anyone's landing, because every reader it admits also
  // opens the customer book. The deal list, a deal and the forecast are
  // siblings of the board in the route tree, so they name themselves as
  // `activePaths` to keep this tab lit on all four addresses.
  if (perms.canOpenPipeline) {
    entries.push({
      to: "/sales/pipeline",
      labelKey: "sales.nav.pipeline",
      activePaths: ["/sales/opportunities", "/sales/forecast"],
    });
  }
  // DOCUMENTS BEFORE CONFIGURATION (P3-C-1). The quote book sits directly
  // after the customer book because they are the same job — a manager moves
  // between "who is this" and "what did we offer them" all day — and both sit
  // ahead of the price-book governance, which is a weekly act by a different
  // desk. The review inbox follows them: it is the DECIDER's screen, offered
  // only to a principal who holds a decide code, so for a plain sales user the
  // strip is unchanged from P3-A plus one entry.
  if (perms.canOpenQuotes) {
    entries.push({ to: "/sales/quotes", labelKey: "sales.nav.quotes" });
  }
  // Orders sit beside quotes because they are the same document one step on,
  // and their audience is WIDER (planner/finance/qc read them) — so for a
  // planner this is the first sales entry that is genuinely theirs.
  if (perms.canOpenOrders) {
    entries.push({ to: "/sales/orders", labelKey: "sales.nav.orders" });
  }
  // The TASK LIST (P3-E PR-E2, D-292 ruling 1) follows the documents it is
  // about: a manager's follow-up work on the quotes and orders beside it. It
  // is offered on `sales.activity.write` — the people tasks are for (ga, fa,
  // sales). A `sales.read` holder outside that set would read a list narrowed
  // to their own tasks, which the system never creates for them: an
  // always-empty list is the disabled shell D-088 forbids.
  if (canOpenTasks(perms)) {
    entries.push({ to: "/sales/tasks", labelKey: "sales.nav.tasks" });
  }
  if (perms.canOpenReviews) {
    entries.push({ to: "/sales/reviews", labelKey: "sales.nav.reviews" });
  }
  // §4.9 ANALYTICS (P3-E PR-E3, D-293) — offered to §4.9's OWN audience
  // ("group_admin, facility_admin; sales sees own row only"), which is
  // `canOpenQuotes`'s set, NOT every `sales.read` holder.
  //
  // The route rides `sales.read` and narrows a non-admin to their own rows,
  // so a planner, qc or storekeeper would be handed a screen that can only
  // ever be empty: none of them OWNS a quote (and since D-337 qc and the
  // storekeeper may not read one at all), and the
  // rollup's grain is the owning manager. That is the always-empty shell
  // D-088 forbids, and the identical argument the task list makes two blocks
  // above. Offering it on `sales.read` was the first cut here and it was
  // wider than the spec.
  if (canOpenAnalytics(perms)) {
    entries.push({ to: "/sales/analytics", labelKey: "sales.nav.analytics" });
  }
  if (perms.canOpenPricing) {
    entries.push({ to: "/sales/price-lists", labelKey: "sales.nav.priceLists" });
    entries.push({ to: "/sales/offcut-classes", labelKey: "sales.nav.offcutClasses" });
  }
  if (perms.has("sales.tier.manage")) {
    entries.push({ to: "/sales/margin-guards", labelKey: "sales.nav.marginGuards" });
  }
  // SETTINGS LAST (P3-E PR-E2, D-292 ruling 11): configuration after
  // configuration. §4.13's audience is everyone with `sales.read` — planner,
  // finance, qc and the storekeeper read the reminder cadence, quiet hours and
  // price toggles that decide what their customers are told — and the screen
  // is read-only for everyone without `sales.tier.manage`. For a storekeeper
  // this is the area's ONE entry, so their app-nav "Sales" link lands here.
  if (canOpenSalesSettings(perms)) {
    entries.push({ to: "/sales/settings", labelKey: "sales.nav.settings" });
  }
  return entries;
}

/** The TASK LIST's audience (P3-E PR-E2, D-292 ruling 1) — the holders of
 * `sales.activity.write`. One predicate for the entry table AND the screen's
 * own refusal, so the two cannot disagree. */
export function canOpenTasks(perms: Pick<SalesEntryPermissions, "has">): boolean {
  // The list itself reads on `sales.read` (`activities.controller.ts`), so
  // the write code alone opens nothing (D-586).
  return perms.has("sales.read") && perms.has("sales.activity.write");
}

/** The ANALYTICS screen's audience (P3-E PR-E3, D-293) — §4.9's own line
 * ("group_admin, facility_admin; sales sees own row only"), which is the quote
 * book's set. One predicate for the entry table AND the screen's own refusal,
 * so the two cannot disagree — the task list's rule, for the same reason.
 *
 * It is deliberately NARROWER than the route's `sales.read`: the server
 * narrows a non-admin to their own rows, and a planner, qc or storekeeper
 * never OWNS a quote, so `sales.read` alone buys them a screen that can only
 * ever be empty (D-088). */
export function canOpenAnalytics(perms: Pick<SalesEntryPermissions, "canOpenQuotes">): boolean {
  return perms.canOpenQuotes;
}

/** The SETTINGS screen's audience (P3-E PR-E2, D-292 ruling 11; spec 09
 * §4.13 "everyone with `sales.read` reads the screen") — the GET's own code.
 * Editing is `sales.tier.manage`'s. */
export function canOpenSalesSettings(perms: Pick<SalesEntryPermissions, "has">): boolean {
  return perms.has("sales.read");
}

/** The PIPELINE screens' audience (spec 09 §4.14, P3-E2 PR E2-1) — the board,
 * the deal list and the deal page: `PIPELINE_SCREEN_ROLES`, the lineage set
 * narrowing 15 admits. One predicate for the entry table AND each screen's own
 * refusal, so the two cannot disagree (the task list's rule). */
export function canOpenPipeline(roles: RoleInput): boolean {
  return hasLineage(roles, PIPELINE_SCREEN_ROLES);
}

/** The FORECAST's audience (spec 09 §4.14, §7 narrowing 7 extended): a VALUE
 * door, so the pipeline audience AND `sales.read_value`. A planner opens the
 * board and the list without money and meets the forecast's one-sentence
 * refusal rather than a request the server answers 403 (D-312 ruling 12). */
export function canOpenForecast(roles: RoleInput, has: (code: string) => boolean): boolean {
  return canOpenPipeline(roles) && has("sales.read") && has("sales.read_value");
}

/** The deal WRITES (create, edit, move, win, lose, reopen, the quote link) —
 * `sales.opportunity.write`. The sales narrowing (assigned customers, deals it
 * reads) is the service's; a screen shows a deal's verbs from the deal's own
 * `mutable` flag, which this cannot know. */
export function canWriteOpportunities(perms: Pick<SalesEntryPermissions, "has">): boolean {
  return perms.has("sales.opportunity.write");
}

/** Who may name ANOTHER owner on a deal (§7 narrowing 16): a LITERAL admin,
 * never a clone of one — the service reads literal `roleCodes` for this
 * exemption (D-224 ruling 7), so the mirror does too. */
export function canReassignOpportunityOwner(roles: RoleInput): boolean {
  // LITERAL-ROLE-CODES: `deal-door.ts` names another owner for the literal admin alone (D-224 ruling 7).
  return hasLiteralRole(roles, ADMIN_ROLES);
}

// ---------------------------------------------------------------------------
// THE CUSTOMER RECORD (P3-E2 PR E2-2a1, D-323). No new code: every helper
// below is a code the mirror already carries, read together with the lineage
// rule the service applies on top of it (D-040's two layers). Each is the one
// predicate the screens and the bulk bar read, so the offer and the server's
// rule cannot drift apart screen by screen.
// ---------------------------------------------------------------------------

/** Spec 09 §7 narrowing 22's writer set: sales (on assigned customers, which
 * the service checks per customer), facility_admin and group_admin. finance
 * holds `sales.manage_customers` for its credit cell alone and is refused 403
 * on every note, pin and tag write, so the code by itself would offer finance
 * an Add box the server always refuses. */
const CUSTOMER_RECORD_WRITE_ROLES: ReadonlySet<string> = new Set([
  "group_admin",
  "facility_admin",
  "sales",
]);

/** Append, pin and unpin a customer's notes (spec 09 §5's E2-2 rows 2 and 3,
 * narrowing 22). The assignment check is the server's: a sales reader on a
 * customer it is not assigned never opens that customer at all. */
export function canWriteCustomerNotes(perms: RoleSignal & Pick<SalesPermissions, "has">): boolean {
  return perms.has("sales.manage_customers") && hasLineage(perms, CUSTOMER_RECORD_WRITE_ROLES);
}

/** Tag and untag a customer, the bulk bar's Add tag and Remove tag, and the
 * Tags cell (spec 09 §5's E2-2 rows 8 and 9, narrowing 22). The same rule as
 * the note writes, kept as its own predicate so the two can part later
 * without a screen reading the wrong one. */
export function canTagCustomers(perms: RoleSignal & Pick<SalesPermissions, "has">): boolean {
  return perms.has("sales.manage_customers") && hasLineage(perms, CUSTOMER_RECORD_WRITE_ROLES);
}

/** Redact a note (spec 09 §5's E2-2 row 3a, narrowing 18): the admin lineage
 * the archive keys on (`notes.service.ts`). Since D-586 the feed carries the
 * lineage, so a tenant's clone of an admin is offered Redact as the server
 * admits it. Built on the recommendation of spec 09 §12's note-redaction
 * question (D-323 ruling 3, owner-pending). */
export function canRedactNotes(roles: RoleInput): boolean {
  return hasLineage(roles, ADMIN_ROLES);
}

/** Curate the tenant's tag list: create, rename, archive (spec 09 §5's E2-2
 * rows 5 to 7) under `sales.tier.manage`, held by group_admin and
 * facility_admin. Every
 * `sales.read` holder of the customer book reads the list. */
export function canCurateTags(perms: Pick<SalesPermissions, "has">): boolean {
  return perms.has("sales.tier.manage");
}

/** The bulk bar's Reassign owner, and the Owner cell (spec 09 §4.1, §7's
 * matrix): naming an owner is the exemption D-224 point 7 keys on the LITERAL
 * role, so a clone of an admin is not offered it. */
export function canBulkReassign(roles: RoleInput): boolean {
  // LITERAL-ROLE-CODES: `assignments.controller.ts` OWNER_SETTING_ROLE_CODES is literal (D-224 ruling 7).
  return hasLiteralRole(roles, ADMIN_ROLES);
}

/** Stands on the group_admin or facility_admin lineage: the key of every
 * admin verb the service checks on ADMIN_LINEAGES or REVOKE_LINEAGES
 * (`customer-door.ts`, `contacts.service.ts`, `overrides.service.ts`), so a
 * tenant's clone of an admin is offered them as the server admits it. */
export function isAdminLineage(roles: RoleInput): boolean {
  return hasLineage(roles, ADMIN_ROLES);
}

/** The bulk bar's Archive (spec 09 §4.1), and the 360's: the archive route's
 * admin-lineage rule (`customers.service.ts` ADMIN_LINEAGES). */
export function canBulkArchive(roles: RoleInput): boolean {
  return hasLineage(roles, ADMIN_ROLES);
}

/** The customer's state the two E2-2b verbs read: its status, and the
 * tombstone pointer a merge sets on the customer that lost it (spec 09 §2.1).
 * Structural, so a screen passes the `Customer` it already holds. */
export interface MergeableCustomer {
  readonly status: string;
  readonly merged_into_customer_id?: string | null;
}

/** Merge (spec 09 §3.13, §4.2's E2-2b annotation, §4.17; narrowing 18): an
 * admin act under `sales.manage_customers` on the archive's admin lineage
 * (D-324 ruling 13), offered on the admin lineage as Archive is, so a
 * tenant's clone of an admin is offered it as the server admits it (D-586). finance holds the route code for its
 * credit cell and is refused, so the code alone would offer it a verb the
 * server always refuses. With no customer this is §4.17's own gate, where
 * anyone else reads a forbidden page. With one it is the 360's offer, which
 * holds an active customer alone, because the merge refuses an archived
 * customer on either side and a merged customer's 360 offers no edit verb. */
export function canMergeCustomers(roles: RoleInput, customer?: MergeableCustomer): boolean {
  if (!hasLineage(roles, ADMIN_ROLES)) return false;
  return customer === undefined || customer.status === "active";
}

/** Restore (spec 09 §3.14, §4.2's E2-2b annotation; narrowing 18): the same
 * admin lineage, offered on an archived customer that was never merged. A
 * merged customer is a tombstone the restore refuses 409 `CUSTOMER_MERGED`
 * (D-324 ruling 14), and `customers_merged_is_archived` holds it archived in
 * the DDL (migration 0079), so the 360 never offers a verb that cannot land. */
export function canRestoreCustomer(roles: RoleInput, customer: MergeableCustomer): boolean {
  return (
    hasLineage(roles, ADMIN_ROLES) &&
    customer.status === "archived" &&
    (customer.merged_into_customer_id ?? null) === null
  );
}

// ---------------------------------------------------------------------------
// ATTACHMENTS (P3-E2 PR E2-2a2, D-323 rulings 8 and 10). No new code here
// either: an attachment write is the write of its subject's own door (§7
// narrowing 21), and a `commercial` file's audience is `sales.read_value`
// (narrowing 19). The service applies the lineage and the door's own
// narrowing per subject, so these decide only what a screen offers.
// ---------------------------------------------------------------------------

/** The three subjects an attachment hangs off (spec 09 §2.29), named as the
 * route's body names them. */
export type AttachmentSubjectKind = "customer" | "quote" | "sales_order";

/** Upload and Detach on a subject's Attached files panel (spec 09 §5's E2-2
 * rows 10 and 13, narrowing 21). A customer's files ride the customer
 * record's writers (narrowing 22), so finance, which holds
 * `sales.manage_customers` for its credit cell alone, is offered nothing. A
 * quote's files ride `sales.quote.write` and an order's `sales.order.write`,
 * which ga, fa and sales hold. qc and the storekeeper hold the order door and
 * neither code, so they list and download an order's files and never write;
 * the quote door refuses them since D-337 (§7 narrowing 23), so they reach no
 * quote's files at all. Whether the door admits this particular document is
 * the server's to say. */
export function canAttachTo(
  perms: RoleSignal & Pick<SalesPermissions, "has">,
  subject: AttachmentSubjectKind,
): boolean {
  switch (subject) {
    case "customer":
      return perms.has("sales.manage_customers") && hasLineage(perms, CUSTOMER_RECORD_WRITE_ROLES);
    case "quote":
      return perms.has("sales.quote.write");
    case "sales_order":
      return perms.has("sales.order.write");
  }
}

/** Whether `commercial` files are this reader's (spec 09 §7 narrowing 19,
 * D-323 ruling 10 as D-335 R1 answered it). A holder of `sales.read_value`
 * lists and downloads them, and any other reader is told how many the list
 * withheld (`withheld_count`) in their place. */
export function canReadCommercial(perms: Pick<SalesPermissions, "has">): boolean {
  return perms.has("sales.read_value");
}

// ---------------------------------------------------------------------------
// THE TAB STRIP (P3-UX, D-445): eleven screens under at most six tabs.
// ---------------------------------------------------------------------------

/** The tabs the strip groups screens under. */
export type SalesNavGroup = "pipeline" | "tasks" | "settings";

/** Which group a screen's path belongs to; a path in no group is a tab of its
 * own. Analytics joins the pipeline because both say how the deals are doing,
 * reviews join tasks because both are work waiting for a person, and the four
 * configuration screens share Settings. */
const SALES_NAV_GROUP_OF: ReadonlyMap<string, SalesNavGroup> = new Map([
  ["/sales/pipeline", "pipeline"],
  ["/sales/analytics", "pipeline"],
  ["/sales/tasks", "tasks"],
  ["/sales/reviews", "tasks"],
  ["/sales/price-lists", "settings"],
  ["/sales/offcut-classes", "settings"],
  ["/sales/margin-guards", "settings"],
  ["/sales/settings", "settings"],
]);

/** Each group's own label and the order its screens are listed in. */
const SALES_NAV_GROUPS: Readonly<
  Record<SalesNavGroup, { readonly labelKey: string; readonly order: readonly string[] }>
> = {
  pipeline: { labelKey: "sales.nav.pipeline", order: ["/sales/pipeline", "/sales/analytics"] },
  tasks: { labelKey: "sales.nav.tasks", order: ["/sales/tasks", "/sales/reviews"] },
  settings: {
    labelKey: "sales.nav.settings",
    order: [
      "/sales/price-lists",
      "/sales/offcut-classes",
      "/sales/margin-guards",
      "/sales/settings",
    ],
  },
};

/** Inside its group the general settings screen is "General", beside "Price
 * lists"; alone (the storekeeper's one entry) it is still "Settings". */
const SALES_NAV_LABEL_IN_GROUP: ReadonlyMap<string, string> = new Map([
  ["/sales/settings", "sales.nav.general"],
]);

export interface SalesNavTab {
  /** Stable key for the tab's test id (`sales-tab-<key>`). */
  readonly key: string;
  readonly to: string;
  readonly labelKey: string;
  readonly activePaths?: readonly string[];
  /** The group's screens when the reader may open two or more of them. The
   * screens draw these as a second strip under their header. */
  readonly children?: readonly SalesAreaEntry[];
}

/**
 * The strip's tabs for this principal (pure). The screens are
 * `salesAreaEntries`, unchanged, so the landing order and every gate stay
 * where they were; this only folds them into groups. A group the reader can
 * open one screen of is that screen's own tab, under the screen's own label.
 */
export function salesNavTabs(perms: SalesEntryPermissions): SalesNavTab[] {
  const tabs: SalesNavTab[] = [];
  const seen = new Set<SalesNavGroup>();
  for (const entry of salesAreaEntries(perms)) {
    const group = SALES_NAV_GROUP_OF.get(entry.to);
    if (group === undefined) {
      tabs.push({ key: entry.to.replace("/sales/", ""), ...entry });
      continue;
    }
    if (seen.has(group)) continue;
    seen.add(group);
    const members = salesNavGroupEntries(perms, group);
    const first = members[0];
    if (first === undefined) continue;
    if (members.length === 1) {
      // Alone, the screen keeps its own label ("Settings", not "General").
      tabs.push({ key: group, ...entry });
      continue;
    }
    tabs.push({
      key: group,
      to: first.to,
      labelKey: SALES_NAV_GROUPS[group].labelKey,
      activePaths: members.flatMap((m) => [m.to, ...(m.activePaths ?? [])]),
      children: members,
    });
  }
  return tabs;
}

/** The screens of one group this principal may open, in the group's order,
 * each under its label inside the group. */
export function salesNavGroupEntries(
  perms: SalesEntryPermissions,
  group: SalesNavGroup,
): SalesAreaEntry[] {
  const entries = salesAreaEntries(perms);
  return SALES_NAV_GROUPS[group].order.flatMap((to) => {
    const entry = entries.find((e) => e.to === to);
    if (entry === undefined) return [];
    return [{ ...entry, labelKey: SALES_NAV_LABEL_IN_GROUP.get(to) ?? entry.labelKey }];
  });
}

/** Where `/sales` (and the app-nav link) lands this principal, or `undefined`
 * when the area holds nothing for them. */
export function salesLandingPath(perms: SalesEntryPermissions): string | undefined {
  return salesAreaEntries(perms)[0]?.to;
}

/**
 * Is this principal REFUSED a sales screen — a stable fact, not a failure?
 *
 * `allowed` is the screen's own gate (a role-set fact, a code, or both —
 * screens pass what §4 says about them). True only once the role feed has
 * settled AND did not fail: while it is in flight, or when it FAILED, the
 * answer is "let the request go and render the server's own refusal" — the
 * fail-open direction, because the mirror is a display aid and the server is
 * the authorisation boundary (D-040's two layers). Callers use it twice: to
 * keep a query from firing at all, and to render `ForbiddenState` instead of
 * an error card whose Retry can never succeed.
 */
export function isSalesReadRefused(
  perms: Pick<SalesPermissions, "isResolved" | "isFeedError">,
  allowed: boolean,
): boolean {
  return perms.isResolved && !perms.isFeedError && !allowed;
}
